import React from 'react';
import { useInView } from 'react-intersection-observer';
import { useGlobalContext } from '../../../provider/global/GlobalContext';
import { cloudinaryUrl } from '../../../utils/cloudinary';
import { PreloadImage } from './PreloadImage';
import { checkIsMobile } from '../../../utils/detectBrowser';

// Validation method for images with Nosto domain
// we might need to do some settings in config so we get and array of strings
// that needs to be validated and set as external

const toUrl = (img) => (typeof img === 'string' ? img : img?.url || '');

const checkIfExternal = (img) => {
  const url = toUrl(img);
  if (!url) return false;
  try {
    const { hostname } = new URL(url);
    return /(?:nosto|sirv)\./.test(hostname);
  } catch {
    return false;
  }
};

const getOptimizedImageUrl = (url, opts = {}) => {
  if (!url?.includes('sirv.com')) return url;
  const { sirvWidth, sirvHeight } = opts;
  if (!sirvWidth && !sirvHeight) return url;

  const hasWidth = /[?&]w=/.test(url);
  const hasHeight = /[?&]h=/.test(url);

  if (hasWidth && hasHeight) return url;

  const separator = url.includes('?') ? '&' : '?';
  const params = [];
  if (!hasWidth && sirvWidth) params.push(`w=${sirvWidth}`);
  if (!hasHeight && sirvHeight) params.push(`h=${sirvHeight}`);

  return `${url}${separator}${params.join('&')}`;
};

export const getImageUrl = (configuration, options = {}, image) => {
  if (!image) return null;

  const url = toUrl(image);

  if (typeof image === 'object' && url?.includes('sirv.com')) {
    return getOptimizedImageUrl(url, options);
  }

  const { sirvWidth, sirvHeight, ...restOptions } = options;

  return cloudinaryUrl(
    {
      fetch_format:
        configuration.format && configuration.format === 'svg'
          ? undefined
          : 'auto',
      width: options?.width,
      height: options?.height,
      secure: true,
      ...restOptions,
    },
    'image',
    image,
  );
};

const defaultOps = {
  crop: 'limit',
  width: 980,
  quality: 'auto',
};

export const Image = (
  {
    style,
    image,
    remote,
    className,
    opts,
    alt,
    preload = false,
  },
) => {
  const imageUrlRaw = toUrl(image);
  const { media } = useGlobalContext();
  const { ref, inView } = useInView();
  const isExternal = checkIfExternal(image);
  const isMobile = checkIsMobile();
  const factors = isMobile ? [1] : [1, 2];

  const getOptions = () => ({
    ...defaultOps,
    width: isMobile ? 628 : opts?.width,
    height: isMobile ? 785 : opts?.height,
    quality: 'auto',
    ...(opts ?? {}),
  });
  const getWidth = () => {
    if (imageUrlRaw?.includes('sirv.com')) {
      return opts?.sirvWidth ?? defaultOps.width;
    }
    return getOptions()?.width ?? defaultOps.width;
  };

  const getHeight = () => {
    if (imageUrlRaw?.includes('sirv.com')) {
      return opts?.sirvHeight ?? defaultOps.height;
    }
    return getOptions()?.height ?? defaultOps.height;
  };

  const imageUrl = getImageUrl(media, getOptions(), imageUrlRaw);

  const getSrcSet = (url) =>
    factors
      .map((f) => {
        const isSirv = url?.includes('sirv.com');
        const scaledOpts = {
          ...opts,
          ...(isSirv
            ? {
              sirvWidth: opts?.sirvWidth
                ? Math.round(opts.sirvWidth * f)
                : undefined,
              sirvHeight: opts?.sirvHeight
                ? Math.round(opts.sirvHeight * f)
                : undefined,
            }
            : {
              width: opts?.width
                ? Math.round(opts.width * f)
                : undefined,
              height: opts?.height
                ? Math.round(opts.height * f)
                : undefined,
            }),
        };
        return `${getOptimizedImageUrl(url, scaledOpts)} ${f}x`;
      })
      .join(', ');

  if (remote || isExternal) {
    return (
      <>
        <PreloadImage
          condition={preload}
          href={imageUrl}
          imageSrcSet={getSrcSet(imageUrl)}
        />
        <img
          ref={ref}
          loading={inView ? 'eager' : 'lazy'}
          className={className}
          style={style}
          width={getWidth()}
          height={getHeight()}
          alt={alt}
          src={getOptimizedImageUrl(imageUrl, opts)}
          srcSet={getSrcSet(imageUrl)}
        />
      </>
    );
  }

  return (
    <>
      <PreloadImage
        condition={preload}
        href={imageUrl}
        imageSrcSet={getSrcSet(imageUrl)}
      />
      <img
        ref={ref}
        style={style}
        className={className}
        fetchPriority={preload ? 'high' : 'auto'}
        width={getWidth()}
        loading={inView ? 'eager' : 'lazy'}
        height={getHeight()}
        alt={alt}
        src={imageUrl}
        srcSet={getSrcSet(imageUrl)}
      />
    </>
  );
};